import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ASSETS, BRAND } from "@/lib/brand";
import { ArrowRight } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "client" });
  const [busy, setBusy] = useState(false);
  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await register(form);
      toast.success("Welcome to Wellness Way.");
      navigate(user.role === "coach" ? "/coach" : "/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" style={{ background: "var(--bg)" }}>
      <div className="hidden lg:block relative overflow-hidden" style={{ background: "var(--surface-2)" }}>
        <img src={ASSETS.organicLinen} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute top-8 left-8 right-8 flex justify-between items-center z-10">
          <Link to="/" className="flex items-baseline gap-2">
            <span className="font-script text-3xl" style={{ color: "var(--ink)" }}>{BRAND.name}</span>
            <span className="wordmark-tag">{BRAND.tag}</span>
          </Link>
          <ThemeToggle />
        </div>
        <div className="absolute bottom-12 left-12 right-12 z-10">
          <p className="overline">Apply</p>
          <h2 className="font-display text-4xl mt-4 leading-tight" style={{ color: "var(--ink)" }}>
            A quieter way<br />
            <span className="font-script normal-case" style={{ fontSize: "1.1em", color: "var(--accent)", letterSpacing: 0 }}>to heal.</span>
          </h2>
          <div className="bronze-rule mt-6" />
        </div>
      </div>

      <div className="flex flex-col justify-center px-8 md:px-16 py-12 max-w-xl w-full mx-auto">
        <div className="lg:hidden flex justify-between items-center mb-12">
          <Link to="/" className="flex items-baseline gap-2">
            <span className="font-script text-2xl">{BRAND.name}</span>
            <span className="wordmark-tag">{BRAND.tag}</span>
          </Link>
          <ThemeToggle />
        </div>
        <p className="overline">Apply</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-6 leading-tight">
          Begin your<br />
          <span className="font-script normal-case" style={{ fontSize: "1.1em", color: "var(--accent)", letterSpacing: 0 }}>restoration.</span>
        </h1>
        <div className="bronze-rule mt-6" />

        <form onSubmit={onSubmit} className="mt-12 space-y-7" data-testid="register-form">
          <div>
            <label className="label-luxe">Full name</label>
            <input data-testid="register-name" required className="input-luxe" value={form.name} onChange={change("name")} placeholder="Ava Stone" />
          </div>
          <div>
            <label className="label-luxe">Email</label>
            <input data-testid="register-email" type="email" required className="input-luxe" value={form.email} onChange={change("email")} placeholder="you@studio.com" />
          </div>
          <div>
            <label className="label-luxe">Password</label>
            <input data-testid="register-password" type="password" required minLength={6} className="input-luxe" value={form.password} onChange={change("password")} placeholder="At least 6 characters" />
          </div>
          <div>
            <label className="label-luxe">I am a…</label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {["client", "coach"].map((r) => (
                <button type="button" key={r} data-testid={`register-role-${r}`}
                  className="px-4 py-3 font-label text-[11px] tracking-[0.22em] uppercase transition-colors"
                  onClick={() => setForm({ ...form, role: r })}
                  style={{
                    border: "0.5px solid",
                    borderColor: form.role === r ? "var(--accent)" : "var(--border-strong)",
                    color: form.role === r ? "var(--accent)" : "var(--text-secondary)",
                    background: form.role === r ? "rgba(168,152,128,0.08)" : "transparent",
                  }}>
                  {r === "client" ? "Client" : "Coach"}
                </button>
              ))}
            </div>
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="register-submit">
            {busy ? "Creating..." : "Create account"} <ArrowRight size={12} />
          </button>
        </form>

        <div className="my-10 flex items-center gap-4">
          <div className="h-px flex-1" style={{ background: "var(--border)" }} />
          <span className="font-label text-[10px] tracking-[0.26em] uppercase" style={{ color: "var(--text-tertiary)" }}>or</span>
          <div className="h-px flex-1" style={{ background: "var(--border)" }} />
        </div>
        <button onClick={googleLogin} className="btn-secondary w-full justify-center" data-testid="register-google">
          Continue with Google
        </button>

        <p className="mt-10 font-label text-xs" style={{ color: "var(--text-secondary)" }}>
          Already inside? <Link to="/login" data-testid="link-login" className="text-accent hover:underline uppercase tracking-[0.2em]">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
